import GradientBorder from "@core-components/gradient-border/GradientBorder";
import { useAppNavigate } from "@hooks/language/useAppNavigate";
import useAppTranslation from "@hooks/language/useAppTranslation";
import {
  ActionIcon,
  Button,
  type ButtonProps,
  CopyButton,
} from "@mantine/core";
import { AppNotification } from "@notifications/Notification";
import {
  IconCopy,
  IconCopyCheckFilled,
  IconExternalLink,
} from "@tabler/icons-react";
import type { ReactNode } from "react";

interface ICopyTextProps {
  value: string;
  children: ReactNode;
  isGradient?: boolean;
  openInNewTab?: boolean;
  notificationLabel?: string;
}

const CopyText: React.FC<ICopyTextProps & ButtonProps> = ({
  value,
  children,
  isGradient,
  openInNewTab,
  notificationLabel = "Le text",
  ...rest
}) => {
  const { t } = useAppTranslation("pages.clientDashboard.components");
  const { navigate } = useAppNavigate();

  const copyText = (copy: () => void) => {
    copy();
    AppNotification.default({
      title: t("copyText.notificationTitle"),
      message: `${notificationLabel} ${t("copyText.notificationMessage")}`,
    });
  };

  const button = (copied: boolean, copy: () => void) => (
    <Button
      c={"#C3C3C3"}
      color="#202125"
      classNames={{
        label: "font-normal",
      }}
      rightSection={
        <>
          {copied ? <IconCopyCheckFilled size={15} /> : <IconCopy size={15} />}
          {openInNewTab && (
            <ActionIcon
              component="a"
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                navigate(value, { openInNewTab: true });
              }}
            >
              <IconExternalLink size={15} />
            </ActionIcon>
          )}
        </>
      }
      {...rest}
      onClick={() => copyText(copy)}
    >
      {children}
    </Button>
  );

  return (
    <CopyButton value={value}>
      {({ copy, copied }) =>
        isGradient ? (
          <GradientBorder
            className="gradient-gray-to-light-white hover-scale rounded-[4px]"
            childrenClassName="p-[1px] !bg-[#202125]"
          >
            {button(copied, copy)}
          </GradientBorder>
        ) : (
          button(copied, copy)
        )
      }
    </CopyButton>
  );
};

export default CopyText;
